import { Box } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useNowEveryMinute } from "../../../../hooks/useNowEveryMinute";
import { NEUTRAL } from "../../../../theme/tokens";
import {
	type OpenShiftRequest,
	type ReplaceShiftRequest,
	type RequestLedger,
	RequestOriginsStatus,
	type ShiftRequest,
	ShiftType,
} from "../../../../utils/models";
import { RequestOriginsLoadNotice } from "../../../RequestData/RequestOrigin/RequestOriginsLoadNotice";
import { clearOriginSelection, type OriginFilterState, originSelectionOf } from "../../../RequestData/RequestOrigin/requestOriginMeta";
import { matchesResponseFilter, type RequestFilter } from "../../../RequestData/RequestsTab/utils";
import type { FlatSwitchOption } from "../../../UIToolkit/Clickable/SystemFlatSwitch";
import type { ShiftTypeFilterValue } from "../../../UIToolkit/molecules/ShiftTypeFilterChips";
import { GapsListScroller } from "../GapsListScroller";
import { groupRequestsByStaffingDay } from "../gapsRequests";
import { GapsRequestSheets } from "../sheets/GapsRequestSheets";
import { GapsSheetKind, openGapsSheet, useCloseGapsSheetOnUnmount } from "../useGapsSheet";
import { GapsOpenShiftsFilterSheet, type GapsRequestsFilterControl } from "./GapsOpenShiftsFilterSheet";
import { GapsOpenShiftsHeader } from "./GapsOpenShiftsHeader";
import { GapsOpenShiftsList } from "./GapsOpenShiftsList";
import { countByResponseFilter } from "./gapsOpenShiftsModel";
import type { GapsApplicant } from "./useGapsApplicants";
import { useGapsOpenShiftsLoad } from "./useGapsOpenShiftsLoad";

type Props = {
	/** The requests after every filter but the response switch — `RequestsTab`'s own narrowing, so both layouts agree. */
	requests: ShiftRequest[];
	responseFilter: RequestFilter;
	onResponseFilterChange: (next: RequestFilter) => void;
	roleGroupFilter: GapsRequestsFilterControl<string[]>;
	jobFilter: GapsRequestsFilterControl<string[]>;
	shiftTypeFilter: GapsRequestsFilterControl<ShiftTypeFilterValue>;
	/** Where the request origins stand: the Origin filter once they are in. */
	originFilter: OriginFilterState;
	/** Why each request exists; empty where the department does not get origins or they haven't loaded. */
	originsByRequestId: ReadonlyMap<string, RequestLedger>;
	/** Requests with a robo call already out (the call-off agent's list), as the classic tab marks them. */
	requestsAgentIds: string[] | undefined;
};

/** The sheets this page hosts — and so the only intents its unmount may close (see `useCloseGapsSheetOnUnmount`). */
const HOSTED_SHEETS: readonly GapsSheetKind[] = [GapsSheetKind.PostedActions, GapsSheetKind.ApproveConfirm];

/**
 * Open Shifts on the gaps-first view (P3-12): the Requests screen's own requests and filters, drawn for the decision a
 * head nurse makes on a phone — who asked, for which shift, and how soon it starts.
 *
 * Requests are grouped by the staffing day they are drawn on (Today / Tomorrow / the date), earliest first, and a day
 * holding an urgent claim is drawn as the 48h band. Each card is the shift (tap → the posted-shift sheet) with the
 * people who asked under it, each with a labelled Approve (→ the confirm sheet). The response switch counts what each of
 * its options lists; the job / role-group and Day / Night filters live behind the Filter button, unchanged.
 */
export const GapsOpenShifts: FC<Props> = ({
	requests,
	responseFilter,
	onResponseFilterChange,
	roleGroupFilter,
	jobFilter,
	shiftTypeFilter,
	originFilter,
	originsByRequestId,
	requestsAgentIds,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
	const departmentId = chosenDepartment.department_id;

	useCloseGapsSheetOnUnmount(HOSTED_SHEETS);
	// A failed or unsettled load is not an empty list: the counts, the summary and "nothing open" wait for a real answer.
	const load = useGapsOpenShiftsLoad();
	const counts = useMemo(() => countByResponseFilter(requests), [requests]);
	const shown = useMemo<ShiftRequest[]>(() => requests.filter((request) => matchesResponseFilter(request, responseFilter)), [requests, responseFilter]);

	// One "now" for the whole list, so "Today", the 48h band and every "starts in" agree with each other — moved on each
	// minute (and on focus), so they stay true while the screen sits open.
	const now = useNowEveryMinute();
	const days = useMemo(() => {
		const rule = { dayStart: chosenDepartment.dayStart, scheduled_shift_delta_minutes: chosenDepartment.scheduled_shift_delta_minutes };
		return groupRequestsByStaffingDay(shown, now, rule);
	}, [shown, now, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes]);
	// DEFAULT: while the call-off agent's list loads (or the flag is off, and it is never fetched) no robo call is known to
	// be out — the Requests screen's own button reads it the same way.
	const robo = useMemo(() => new Set(requestsAgentIds), [requestsAgentIds]);

	const isFilterApplied =
		roleGroupFilter.selected.length > 0 ||
		jobFilter.selected.length > 0 ||
		shiftTypeFilter.selected !== ShiftType.All ||
		(originFilter.status === RequestOriginsStatus.Ready && originFilter.control.selected.length > 0);
	// The pill also counts an origin choice held while origins reload or fail, so Clear stays in reach; the empty state
	// speaks only of filters that are narrowing the list now.
	const isNarrowed = isFilterApplied || originSelectionOf(originFilter).length > 0;

	const handleResponseFilter = useCallback(
		(option: FlatSwitchOption<RequestFilter>): void => {
			track("gaps_requests_filter_changed", { department_id: departmentId, filter: option.value });
			onResponseFilterChange(option.value);
		},
		[departmentId, onResponseFilterChange, track],
	);

	const clearFilters = useCallback((): void => {
		track("gaps_requests_filter_cleared", { department_id: departmentId });
		roleGroupFilter.onChange([]);
		jobFilter.onChange([]);
		shiftTypeFilter.onChange(ShiftType.All);
		clearOriginSelection(originFilter);
	}, [departmentId, jobFilter, roleGroupFilter, shiftTypeFilter, originFilter, track]);

	const handleOpenRequest = useCallback(
		(request: OpenShiftRequest | ReplaceShiftRequest): void => {
			track("gaps_requests_request_opened", { department_id: departmentId, request_id: request.id, request_type: request.request_type });
			openGapsSheet({ kind: GapsSheetKind.PostedActions, requestId: request.id });
		},
		[departmentId, track],
	);

	const handleApprove = useCallback(
		(request: OpenShiftRequest | ReplaceShiftRequest, applicant: GapsApplicant): void => {
			track("gaps_requests_approve_clicked", { department_id: departmentId, request_id: request.id, nurse_id: applicant.nurseId });
			openGapsSheet({ kind: GapsSheetKind.ApproveConfirm, requestId: request.id, nurseId: applicant.nurseId });
		},
		[departmentId, track],
	);

	const emptyTitle = requests.length === 0 ? (isFilterApplied ? t("gaps.requests.emptyFiltered") : t("gaps.requests.emptyNone")) : t("gaps.requests.emptyTab");

	return (
		<Box data-testid="gaps-requests-root" sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, width: "100%", bgcolor: NEUTRAL[50] }}>
			<GapsOpenShiftsHeader
				load={load}
				counts={counts}
				responseFilter={responseFilter}
				onResponseFilter={handleResponseFilter}
				isNarrowed={isNarrowed}
				onOpenFilter={() => setIsFilterOpen(true)}
			/>

			{originFilter.status === RequestOriginsStatus.Failed && (
				<Box sx={{ mx: 1.5, mt: 1, flexShrink: 0 }}>
					<RequestOriginsLoadNotice departmentId={departmentId} retry={originFilter.retry} />
				</Box>
			)}

			<GapsListScroller idPrefix="gaps-requests">
				<GapsOpenShiftsList
					load={load}
					days={days}
					emptyTitle={emptyTitle}
					isNarrowed={isFilterApplied}
					responseFilter={responseFilter}
					roboCallRequestIds={robo}
					originsByRequestId={originsByRequestId}
					now={now}
					onOpenRequest={handleOpenRequest}
					onApprove={handleApprove}
				/>
			</GapsListScroller>

			<GapsOpenShiftsFilterSheet
				open={isFilterOpen}
				onClose={() => setIsFilterOpen(false)}
				roleGroupFilter={roleGroupFilter}
				jobFilter={jobFilter}
				shiftTypeFilter={shiftTypeFilter}
				originFilter={originFilter}
				onClear={clearFilters}
			/>
			<GapsRequestSheets />
		</Box>
	);
};
